import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, Camera, ShieldCheck, Lock } from "lucide-react";
import { api } from "@/lib/api";

const DEFAULT_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/a4e7a419-71b0-4f59-8501-640e90382c41/images/8c481a12161d5d6e8c66eaf357d0199d60af674d27695e36a50eeecba9b264c9.jpeg";

/** Fisher–Yates shuffle (returns a new array, original untouched). */
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Lower hero / animated footer section:
 *  - Crossfading slideshow background (random order each load) with Ken Burns per slide
 *  - Dark overlay (admin opacity)
 *  - Full honeycomb of complete hexagons with continuous chasing strokes
 *  - Content (heading, copy, CTAs)
 *  - Bottom brand row
 * Respects prefers-reduced-motion.
 */
export default function FooterHero({ onStartFull, onStartPhotos, images = [] }) {
  const [s, setS] = useState({
    footer_image_url: DEFAULT_IMAGE,
    footer_overlay_opacity: 0.7,
    footer_accent_color: "#F59E0B",
    footer_heading: "Submit better project information, faster.",
    footer_text:
      "Upload the details once and create a clean project record that can support case studies, website content and future marketing.",
    footer_button_full_label: "Start Full Submission",
    footer_button_photos_label: "Start With Photos",
  });

  useEffect(() => {
    api.get("/settings").then((r) => {
      const d = r.data || {};
      setS((prev) => ({
        ...prev,
        ...{
          footer_image_url: d.footer_image_url || prev.footer_image_url,
          footer_overlay_opacity:
            typeof d.footer_overlay_opacity === "number"
              ? d.footer_overlay_opacity
              : prev.footer_overlay_opacity,
          footer_accent_color: d.footer_accent_color || prev.footer_accent_color,
          footer_heading: d.footer_heading || prev.footer_heading,
          footer_text: d.footer_text || prev.footer_text,
          footer_button_full_label:
            d.footer_button_full_label || prev.footer_button_full_label,
          footer_button_photos_label:
            d.footer_button_photos_label || prev.footer_button_photos_label,
        },
      }));
    });
  }, []);

  /* Build the slideshow image list: prefer prop images, fall back to settings image. */
  const slideshow = useMemo(() => {
    const base = images && images.length > 0 ? images : [s.footer_image_url];
    return shuffle(base);
  }, [images, s.footer_image_url]);

  /* Each slide is on for SLIDE_DURATION; total cycle = N * SLIDE_DURATION. */
  const SLIDE_DURATION = 7; // seconds visible per slide
  const total = slideshow.length * SLIDE_DURATION;

  return (
    <section
      className="fh-section"
      data-testid="footer-hero"
      style={{ "--fh-accent": s.footer_accent_color }}
    >
      <div className="fh-image-wrap">
        {slideshow.map((url, i) => (
          <div
            key={`${url}-${i}`}
            className="fh-image"
            style={{
              backgroundImage: `url('${url}')`,
              animationDuration: `${total}s`,
              animationDelay: `${i * SLIDE_DURATION}s`,
              "--slide-count": slideshow.length,
            }}
          />
        ))}
      </div>

      <div className="fh-overlay" style={{ opacity: s.footer_overlay_opacity }} />

      <HexagonOverlay accent={s.footer_accent_color} />

      <div className="fh-content">
        <div className="fh-inner">
          <div className="fh-eyebrow" style={{ color: s.footer_accent_color }}>
            HTM Project Hub
          </div>
          <h2 className="fh-heading">{s.footer_heading}</h2>
          <p className="fh-text">{s.footer_text}</p>
          <div className="fh-actions">
            <button
              type="button"
              className="fh-btn-primary"
              onClick={onStartFull}
              data-testid="footer-start-full"
            >
              {s.footer_button_full_label}
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="fh-btn-ghost"
              onClick={onStartPhotos}
              data-testid="footer-start-photos"
            >
              <Camera className="h-4 w-4" />
              {s.footer_button_photos_label}
            </button>
          </div>
        </div>
      </div>

      <div className="fh-footer-row">
        <div className="fh-footer-inner">
          <span className="fh-footer-brand">HTM Project Hub</span>
          <span className="fh-footer-badge" data-testid="footer-domain-badge">
            <Lock className="h-3 w-3" />
            <span className="fh-footer-host">htmltd.co.uk</span>
            <span className="fh-footer-dot" aria-hidden="true" />
            <span>Secured</span>
          </span>
          <span className="fh-footer-meta">
            <ShieldCheck className="h-3.5 w-3.5" />
            Project submissions are private and only visible to approved admin users.
          </span>
        </div>
      </div>
    </section>
  );
}

/* --- Honeycomb of complete hexagons in lower-right + lower-centre. --- */
/* All paths are closed regular hexagons. Each gets a faint static stroke
   plus a bright animated chase segment travelling continuously around it.  */

const R = 46; // hexagon radius
const SQRT3 = Math.sqrt(3);

// Pointy-top honeycomb spacing (matches hexPath below — first vertex at 30°)
const STEP_X = SQRT3 * R; // ~79.6  horizontal distance between centres in same row
const STEP_Y = 1.5 * R;   // ~69    vertical distance between rows
const OFFSET_X = STEP_X / 2;

// Build a triangular cluster anchored at bottom-right (heavier), with a
// secondary lighter cluster in the lower centre.
const HEXES = (() => {
  const out = [];
  // Anchor near right edge of viewBox 1200x700
  const ax = 1180;
  const ay = 690;

  // Bottom-right dense triangle (rows of decreasing width going up)
  const rightCluster = [
    { row: 0, count: 5 }, // bottom row, 5 hexagons
    { row: 1, count: 4 },
    { row: 2, count: 4 },
    { row: 3, count: 3 },
    { row: 4, count: 2 },
    { row: 5, count: 1 },
  ];
  rightCluster.forEach(({ row, count }) => {
    const y = ay - row * STEP_Y;
    const xOffset = row % 2 === 0 ? 0 : OFFSET_X;
    for (let i = 0; i < count; i++) {
      out.push({ cx: ax - xOffset - i * STEP_X, cy: y, group: "r" });
    }
  });

  // Lower-centre lighter cluster
  const centerCluster = [
    { cx: 640, cy: 660 },
    { cx: 720, cy: 660 },
    { cx: 800, cy: 660 },
    { cx: 680, cy: 591 },
    { cx: 760, cy: 591 },
    { cx: 720, cy: 522 },
    { cx: 560, cy: 660, mobileHidden: true },
    { cx: 600, cy: 591, mobileHidden: true },
  ];
  centerCluster.forEach((h) => out.push({ ...h, group: "c" }));

  return out;
})();

/* Vary timing / direction per hex so the pattern feels alive. */
const ANIM_PRESETS = [
  { dur: 10, delay: 0.0, dir: "normal" },
  { dur: 13, delay: 0.6, dir: "reverse" },
  { dur: 9, delay: 1.2, dir: "normal" },
  { dur: 15, delay: 0.3, dir: "normal" },
  { dur: 11, delay: 2.0, dir: "reverse" },
  { dur: 17, delay: 0.9, dir: "normal" },
  { dur: 8, delay: 2.4, dir: "reverse" },
  { dur: 14, delay: 1.5, dir: "normal" },
  { dur: 12, delay: 0.0, dir: "reverse" },
  { dur: 16, delay: 1.1, dir: "normal" },
];

/* Subset of indices that use the brand accent colour instead of white. */
const ACCENT_INDICES = new Set([2, 7, 11]);

function HexagonOverlay({ accent }) {
  return (
    <svg
      className="fh-svg"
      viewBox="0 0 1200 700"
      preserveAspectRatio="xMaxYMax slice"
      aria-hidden="true"
    >
      <defs>
        <filter id="fh-soft" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="0.4" />
        </filter>
      </defs>

      {/* Static base triangles (always visible) */}
      <g className="fh-hex-base">
        {HEXES.map((h, i) => (
          <path
            key={`b-${i}`}
            d={triPath(h.cx, h.cy, R, i % 2 === 1)}
            className={h.mobileHidden ? "fh-mobile-hide" : ""}
          />
        ))}
      </g>

      {/* Chasing animated strokes (one per hex, varying timing & direction) */}
      <g className="fh-hex-chase">
        {HEXES.map((h, i) => {
          const preset = ANIM_PRESETS[i % ANIM_PRESETS.length];
          const useAccent = ACCENT_INDICES.has(i);
          const style = {
            "--dur": `${preset.dur}s`,
            "--delay": `${preset.delay}s`,
            animationDirection: preset.dir,
            stroke: useAccent ? accent : "rgba(255,255,255,0.65)",
          };
          return (
            <path
              key={`c-${i}`}
              d={triPath(h.cx, h.cy, R, i % 2 === 1)}
              pathLength="100"
              className={`fh-chase ${h.mobileHidden ? "fh-mobile-hide" : ""}`}
              style={style}
            />
          );
        })}
      </g>

      {/* Node dots at a selection of hexagon corners */}
      <g className="fh-nodes">
        {HEXES.filter((_, i) => i % 3 === 0).map((h, i) => {
          // apex vertex of the triangle
          return <circle key={`n-${i}`} cx={h.cx} cy={h.cy - R} r="2" />;
        })}
      </g>
    </svg>
  );
}

/** Closed equilateral triangle path centred on (cx, cy). flip=true points down. */
function triPath(cx, cy, r, flip = false) {
  const start = flip ? Math.PI / 2 : -Math.PI / 2;
  const pts = [];
  for (let i = 0; i < 3; i++) {
    const a = start + ((Math.PI * 2) / 3) * i;
    pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  return (
    `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)} ` +
    pts.slice(1).map((p) => `L ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ") +
    " Z"
  );
}
